<template>
  <ts-loading-banner :loading="loading">
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('projectProfile.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('projectProfile.pageTitle'),
          href: '/admin/project-profiles'
        },
        {
          text: $t('view'),
          active: true
        }
      ]" />
    </div>
    <div class="ts-bg-white ts-p-4 ts-rounded-md">
      <div class="ts-flex ts-justify-between">
        <div class="ts-flex ts-space-x-4">
          <div class="ts-w-12 ts-h-12 ts-bg-gray-200 ts-rounded-full ts-p-2 ts-flex ts-items-center ts-justify-center">
            <Icon type="ios-folder-open" size="30" />
          </div>
          <div>
            <h1 class="ts-font-robot ts-text-2xl ts-text-gray-700 ts-mb-0">
              {{ resources ? resources.project_name : '' }}
              <a href="#" @click.prevent="onEdit">
                <Icon type="ios-create-outline" />
              </a>
            </h1>
            <div>
              <p class="ts-mb-0">
                <Icon type="ios-contact-outline" size="15" />
                {{ resources ? resources.owner_name_en : '' }}
              </p>
            </div>
          </div>
        </div>
        <div class="ts-grid ts-grid-cols-5 ts-gap-3 ts-border ts-shadow ts-p-3 ts-rounded-md ts-h-16 ts-bg-gray-200">
          <div class="c100 small" :class="[`p${percentage}`]">
            <span class="ts-font-mono ts-text-black"> {{ percentage }}%</span>
            <div class="slice">
              <div class="bar"></div>
              <div class="fill"></div>
            </div>
          </div>
          <div class="ts-space-y-2">
            <strong class="ts-font-robot">Due Date</strong>
            <p class="ts-mb-0" style="padding-top: 2px;">
              {{ resources ? resources.estimated_end_date : 'N/A' }}
            </p>
          </div>
          <div class="ts-space-y-2">
            <strong class="ts-font-robot ts-text-center">Passed</strong>
            <p class="ts-mb-0 ts-text-center ts-bg-red-600 ts-text-white ts-rounded-md" style="padding-top: 2px;">
              {{ passedDay }}
            </p>
          </div>
          <div class="ts-space-y-2">
            <strong class="ts-font-robot">Milestone</strong>
            <p class="ts-mb-0 ts-text-center ts-bg-gray-600 ts-text-white ts-rounded-md" style="padding-top: 8px;">
              {{ resources ? resources.milestones_count : 0 }}
              <span class="ts-bg-green-600 ts-block ts-rounded-b ts-font-hanuman ts-text-base">
                {{ resources ? resources.milestone_completed_count : 0 }}
              </span>
            </p>
          </div>
          <div class="ts-space-y-2">
            <strong class="ts-font-robot">Tasks</strong>
            <p class="ts-mb-0 ts-text-center ts-bg-gray-600 ts-text-white ts-rounded-md" style="padding-top: 8px;">
              <span title="All task">{{
                resources ? resources.tasks_count : 0
              }}</span>
              <span class="ts-bg-green-600 ts-block ts-rounded-b ts-font-hanuman ts-text-base">
                {{ resources ? resources.task_completed_count : 0 }}
              </span>
            </p>
          </div>
        </div>
      </div>

      <Tabs v-model="active_tab" @on-click="onTabChange">
        <TabPane :label="$t('projectProfile.detail')" icon="ios-book" name="detail">
          <div style="min-height: 500px;">
            <ViewDetailTab :resources="resources" v-if="resources !== undefined" />
          </div>
        </TabPane>
        <TabPane :label="$t('projectProfile.member')" icon="ios-contacts" name="member">
          <MemberTab :resources="resources" @fetch="fetchData" />
        </TabPane>
        <TabPane :label="$t('projectProfile.milestone')" icon="ios-bookmark" name="milestone">
          <!-- <div class="ts-relative"> -->
          <MilestoneTab :milestones="milestones" @fetchData="fetchData" @fetchMileStone="fetchMileStone" />
          <!-- </div> -->
        </TabPane>
        <TabPane :label="$t('projectProfile.task')" icon="ios-menu" name="task">
          <TaskProfile v-if="active_tab == 'task'" />
        </TabPane>
        <TabPane :label="$t('projectProfile.subTask')" icon="ios-pricetag" name="subtask">
          <SubTaskTable v-if="active_tab == 'subtask'" />
        </TabPane>
        <TabPane :label="$t('projectProfile.history')" icon="ios-clock" name="history">
          <HistoryTab v-if="active_tab == 'history'" />
        </TabPane>
      </Tabs>
    </div>
    <a-modal v-if="showUpdateForm" v-model="showUpdateForm" :title="$t('projectProfile.pageTitle')" :footer="null"
      :centered="true" :maskClosable="false" :zIndex="1000" width="50%" @cancel="onCancelUpdate">
      <UpdateForm @cancel="onCancelUpdate" :updateData="resources" />
    </a-modal>
  </ts-loading-banner>
</template>

<script>
import { mapActions } from 'vuex'
import ViewDetailTab from './components/view-detail-tab.vue'
import MilestoneTab from './components/milestone-tab.vue'
import TaskProfile from './components/task-profile.vue'
import HistoryTab from './components/history-tab.vue'
import MemberTab from './components/member-tab.vue'
import moment from 'moment'
import UpdateForm from './update-form.vue'
import SubTaskTable from './components/sub-task/table.vue'

export default {
  name: 'viewProject',
  components: {
    ViewDetailTab,
    MilestoneTab,
    TaskProfile,
    HistoryTab,
    MemberTab,
    UpdateForm,
    SubTaskTable
  },
  data() {
    return {
      loading: false,
      resources: undefined,
      active_tab: undefined,
      showUpdateForm: false,
      milestones: []
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    passedDay() {
      if (!this.resources) return 'N/A'
      if (this.resources.estimated_end_date) {
        const specificDate = moment(
          this.resources.estimated_end_date,
          'DD-MM-YYYY'
        )
        const currentDate = moment()
        const dayDifference = currentDate.diff(specificDate, 'days')

        return (dayDifference > 0 ? dayDifference : 0) + ' Days'
      }
      return 'N/A'
    },
    percentage() {
      var row = this.resources
      if (!row) return 0
      if (row.task_completed_count) {
        return Math.round((row.task_completed_count / row.tasks_count) * 100)
      }
      return 0
    }
  },
  methods: {
    ...mapActions('projectMgt/projectProfile', ['find']),
    ...mapActions('projectMgt/projectMilestone', ['fetch']),
    fetchData() {
      this.loading = true
      this.find(this.$route.params.id)
        .then(response => {
          this.resources = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    fetchMileStone() {
      this.milestones = undefined
      this.loading = true
      this.fetch({
        filter: {
          project_id: this.$route.params.id
        }
      })
        .then(response => {
          this.milestones = response.data
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    onClose() {
      this.$emit('close', false)
      this.resources = {}
    },
    onTabChange(name) {
      this.active_tab = name
      switch (name) {
        case 'detail':
          if (this.resources == undefined) {
            return this.fetchData()
          }
          break

        case 'milestone':
          if (!this.milestones.length) {
            return this.fetchMileStone()
          }
      }
    },
    onCancelUpdate() {
      this.showUpdateForm = false
      this.fetchData()
    },
    onEdit() {
      this.showUpdateForm = true
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      switch (from.name) {
        case 'task-edit':
          vm.onTabChange('task');
          break;
        case 'project-milestone-view':
          vm.onTabChange('milestone');
          break;
        default:
          break;
      }
    })
  },
}
</script>

<style scoped>
@import './circle.css';
</style>
